"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";

export function disablingUserRequiresPrimaryPayer(
  user: RegistrantAccountUserResponseSchema,
  users: readonly RegistrantAccountUserResponseSchema[],
): boolean {
  return (
    user.status === "active" &&
    Boolean(user.can_make_payments) &&
    !users.some(
      (candidate) =>
        candidate.id !== user.id &&
        candidate.status === "active" &&
        candidate.can_make_payments,
    )
  );
}

interface DisableUserDialogProps {
  user: RegistrantAccountUserResponseSchema | null;
  users: readonly RegistrantAccountUserResponseSchema[];
  isLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function DisableUserDialog({
  user,
  users,
  isLoading,
  onOpenChange,
  onConfirm,
}: DisableUserDialogProps) {
  const movesPaymentPermission = Boolean(
    user && disablingUserRequiresPrimaryPayer(user, users),
  );
  const userName = user?.name || user?.email || "this user";

  return (
    <AlertDialog open={user !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disable user</AlertDialogTitle>
          <AlertDialogDescription>
            Disable {userName}? They will no longer be able to log in. Existing
            registrations and history will be preserved.
            {movesPaymentPermission
              ? " This user is the only person currently allowed to make payments, so the primary user will be given payment permission."
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            type="button"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/80"
            disabled={isLoading}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
          >
            {isLoading ? "Disabling…" : "Disable"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
